/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { UserManager, UserManagerSettings, User } from 'oidc-client';
import { Observable, ReplaySubject, from, map, take } from 'rxjs';
import { ConfigService } from '@app/shared/services/config/config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private oidcManager = new UserManager(this.getUserManagerSettings());
  private oidcUserSubject$: ReplaySubject<User | null> = new ReplaySubject<User | null>(1);

  constructor(private configService: ConfigService) {
    this.loadUser();
  }

  getUser$(): Observable<User | null> {
    return this.oidcUserSubject$.asObservable();
  }

  isLoggedIn$(): Observable<boolean> {
    return this.getUser$()
      .pipe(
        map(user => {
          if (user) {
            return !user.expired;
          }
          return false;
        })
      );
  }

  getAuthorizationHeaderValue$(): Observable<string> {
    return this.getUser$()
      .pipe(
        take(1),
        map(user => {
          if (user) {
            return `${user.token_type} ${user.access_token}`;
          }
          return '';
        })
      );
  }

  signIn$(): Observable<User | null> {
    const promise = this.oidcManager.signinPopup()
      .then(user => {
        this.oidcUserSubject$.next(user);
        return user;
      })
      .catch(() => null);
    return from(promise)
      .pipe(take(1));
  }

  signOut$(): Observable<void> {
    const promise = this.oidcManager.signoutRedirect()
      .then(() => {
        this.oidcUserSubject$.next(null);
      });
    return from(promise)
      .pipe(take(1));
  }

  signInSilent$(token: string): Observable<User | null> {
    const promise = this.oidcManager
      .signinSilent({ acr_values: token })
      .then(user => {
        this.oidcUserSubject$.next(user);
        return user;
      })
      .catch(() => null);
    return from(promise)
      .pipe(take(1));
  }

  private loadUser(): void {
    this.oidcManager.clearStaleState()
      .then(() => this.oidcManager.getUser())
      .then(user => {
        if (user) {
          return user;
        }
        return this.oidcManager.querySessionStatus()
          .then(() => this.oidcManager.signinSilent())
          .catch(() => null);
      })
      .then(user => {
        this.oidcUserSubject$.next(user);
      });
  }

  private getUserManagerSettings(): UserManagerSettings {
    return {
      authority: this.configService.config.authorityUri,
      post_logout_redirect_uri: `${location.protocol}//${location.host}`,
      silent_redirect_uri: `${location.protocol}//${location.host}/assets/silent_renew.html`,
      popup_redirect_uri: `${location.protocol}//${location.host}/assets/popup.html`,
      client_id: this.configService.config.clientId,
      response_type: 'id_token token',
      scope: 'openid profile dvi.bjootify.api',
      filterProtocolClaims: true,
      loadUserInfo: true,
      automaticSilentRenew: true,
      includeIdTokenInSilentRenew: true,
      revokeAccessTokenOnSignout: true
    };
  }
}
